import {action, computed, makeObservable, observable} from 'mobx';
import I18n from 'app/i18n';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import {getLinkPath} from 'app/utils';
import ArticleInterface from 'app/interfaces/articles/ArticleInterface';
import {
    buildCategoryOptionsFromFaqItems,
    buildKnownCategoryKeys,
    isFaqCategoryKey,
    type FaqCategoryKey,
    type FaqCategoryOption
} from 'app/utils/knowledgeCenter/categories';
import {
    getPopulatedFaqCategories,
    groupFaqsByCategory,
    searchFaqs as searchFaqsInList,
    selectPopularFaqs,
    type FaqArticleLink,
    type FaqCategorySummary,
    type FaqSearchableItem,
    type FaqsByCategory
} from 'app/utils/knowledgeCenter/faqGrouping';

export interface FaqInterface {
    title: string
    text: string
    name: string
    category?: string
    popular?: boolean
    sequence?: number
}

export type FaqSummaryInterface = Omit<FaqInterface, 'text'>;

export interface FaqHelpfulVoteData {
    helpful_yes_count: number
    helpful_no_count: number
    guest_voter_id?: string
}

const GUEST_VOTER_KEY_PREFIX = 'guest:';
const FAQ_GUEST_VOTER_KEY_STORAGE = 'faq_guest_voter_key';
const HTTP_STATUS_CONFLICT = 409;

export interface FaqHelpfulVoteResult {
    success: boolean
    duplicate: boolean
    message: string
    data?: FaqHelpfulVoteData
}

interface HelpfulVoteResponse {
    success: boolean
    data?: FaqHelpfulVoteData
}

interface HelpfulVoteStatusResponse {
    success: boolean
    data?: {
        voted: boolean
    }
}

interface FaqMetaResponse {
    success?: boolean
    data?: {
        categories: FaqCategoryOption[]
    }
}

export class FaqStore {
    @observable faqs: FaqInterface[] = [];

    @observable summaryFaqs: FaqSummaryInterface[] = [];

    @observable asideHiddenTemples: ArticleInterface[] = [];

    @observable loading: boolean = true;

    @observable summaryLoading: boolean = false;

    @observable voteError: string | null = null;

    @observable categoryOptions: FaqCategoryOption[] = [];

    @observable categoryMetaLoading: boolean = false;

    @observable faqsLoaded: boolean = false;

    @observable summaryLoaded: boolean = false;

    private faqsLoadPromise: Promise<void> | null = null;

    private summaryLoadPromise: Promise<void> | null = null;

    private metaLoadPromise: Promise<void> | null = null;

    private sessionGuestVoterKey: string | null = null;

    constructor() {
        makeObservable(this);
        this.sessionGuestVoterKey = this.loadPersistedGuestVoterKey();
    }

    @computed
    get resolvedCategoryOptions(): FaqCategoryOption[] {
        if (this.categoryOptions.length > 0) {
            return this.categoryOptions;
        }

        return buildCategoryOptionsFromFaqItems(this.faqListForGrouping());
    }

    @computed
    get knownCategoryKeys(): Set<FaqCategoryKey> {
        return buildKnownCategoryKeys(this.resolvedCategoryOptions);
    }

    @computed
    get faqsByCategory(): FaqsByCategory {
        const categoryKeys = this.resolvedCategoryOptions.map(option => option.key);

        return groupFaqsByCategory(this.faqListForGrouping(), categoryKeys, this.knownCategoryKeys);
    }

    @computed
    get faqCategories(): FaqCategorySummary[] {
        return getPopulatedFaqCategories(this.faqsByCategory, this.resolvedCategoryOptions);
    }

    @computed
    get popularFaqs(): FaqArticleLink[] {
        return selectPopularFaqs(this.faqListForGrouping()).map(({name, title}) => ({name, title}));
    }

    @action
    private setFaqs(faqs: FaqInterface[]): void {
        this.faqs = faqs;
        this.faqsLoaded = true;
        this.setLoading(false);
    }

    @action
    private setSummaryFaqs(faqs: FaqSummaryInterface[]): void {
        this.summaryFaqs = faqs;
        this.summaryLoaded = true;
        this.summaryLoading = false;
    }

    @action
    setLoading(loading: boolean): void {
        this.loading = loading;
    }

    @action
    setSummaryLoading(loading: boolean): void {
        this.summaryLoading = loading;
    }

    @action
    setVoteError(error: string | null): void {
        this.voteError = error;
    }

    @action
    setCategoryOptions(options: FaqCategoryOption[]): void {
        this.categoryOptions = options;
    }

    @action
    setCategoryMetaLoading(loading: boolean): void {
        this.categoryMetaLoading = loading;
    }

    isKnownCategory(value: string): value is FaqCategoryKey {
        return isFaqCategoryKey(value, this.knownCategoryKeys);
    }

    @action
    updateHiddenTemples = (temples: ArticleInterface[]): void => {
        this.asideHiddenTemples = temples;
    };

    searchFaqs(query: string, category?: FaqCategoryKey): FaqInterface[] {
        const pool = category
            ? this.faqs.filter(faq => faq.category === category)
            : this.faqs;

        return searchFaqsInList(pool, query) as FaqInterface[];
    }

    loadMeta(): Promise<void> {
        if (this.categoryOptions.length > 0) {
            return Promise.resolve();
        }

        if (this.metaLoadPromise) {
            return this.metaLoadPromise;
        }

        this.setCategoryMetaLoading(true);

        const promise = new Get({url: getLinkPath('/faq/meta')})
            .execute()
            .then(response => response.json())
            .then((response: FaqMetaResponse) => {
                const categories = response.data?.categories;

                if (categories && categories.length > 0) {
                    this.setCategoryOptions(categories);
                }
            })
            .catch(() => null)
            .finally(() => {
                this.setCategoryMetaLoading(false);
                this.metaLoadPromise = null;
            });

        this.metaLoadPromise = promise;

        return promise;
    }

    loadData(): Promise<void> {
        return this.loadFaqEndpoint<FaqInterface>({
            apply: response => this.setFaqs(response),
            getInFlight: () => this.faqsLoadPromise,
            isLoaded: () => this.faqsLoaded,
            onCacheHit: () => this.setLoading(false),
            setInFlight: promise => {
                this.faqsLoadPromise = promise;
            },
            setLoading: loading => this.setLoading(loading),
            url: getLinkPath('/faq')
        });
    }

    loadSummary(): Promise<void> {
        return this.loadFaqEndpoint<FaqSummaryInterface>({
            apply: response => this.setSummaryFaqs(response),
            getInFlight: () => this.summaryLoadPromise,
            isLoaded: () => this.summaryLoaded,
            setInFlight: promise => {
                this.summaryLoadPromise = promise;
            },
            setLoading: loading => this.setSummaryLoading(loading),
            url: getLinkPath('/faq/summary')
        });
    }

    async fetchHelpfulVoteStatus(articleName: string): Promise<boolean> {
        try {
            const params: Record<string, string> = {};

            if (this.sessionGuestVoterKey) {
                params.voter_key = this.sessionGuestVoterKey;
            }

            const httpResponse = await new Get({
                params,
                url: getLinkPath(`/faq/${articleName}/helpful_vote_status`)
            }).execute();
            const response = await httpResponse.json() as HelpfulVoteStatusResponse;

            return response.success === true && response.data?.voted === true;
        } catch {
            return false;
        }
    }

    async submitHelpfulVote(articleName: string, helpful: boolean): Promise<FaqHelpfulVoteResult> {
        this.setVoteError(null);

        try {
            const {httpResponse, response} = await this.fetchHelpfulVote(articleName, helpful);

            return this.processHelpfulVoteResponse(httpResponse, response);
        } catch {
            const message = this.helpfulVoteFailureMessage();

            this.setVoteError(message);

            return {
                duplicate: false,
                message,
                success: false
            };
        }
    }

    private faqListForGrouping(): FaqSearchableItem[] {
        return this.summaryFaqs.length > 0 ? this.summaryFaqs : this.faqs;
    }

    private loadFaqEndpoint<T extends FaqInterface | FaqSummaryInterface>(config: {
        apply: (items: T[]) => void
        getInFlight: () => Promise<void> | null
        isLoaded: () => boolean
        onCacheHit?: () => void
        setInFlight: (promise: Promise<void> | null) => void
        setLoading: (loading: boolean) => void
        url: string
    }): Promise<void> {
        if (config.isLoaded()) {
            config.onCacheHit?.();

            return Promise.resolve();
        }

        const inFlight = config.getInFlight();

        if (inFlight) {
            return inFlight;
        }

        config.setLoading(true);
        const promise = new Get({url: config.url})
            .execute()
            .then(response => response.json())
            .then((response: T[]) => {
                config.apply(response);
            })
            .finally(() => {
                config.setLoading(false);
                config.setInFlight(null);
            });

        config.setInFlight(promise);

        return promise;
    }

    private buildHelpfulVoteParams(helpful: boolean): Record<string, boolean | string> {
        const params: Record<string, boolean | string> = {helpful};

        if (this.sessionGuestVoterKey) {
            params.voter_key = this.sessionGuestVoterKey;
        }

        return params;
    }

    private async fetchHelpfulVote(
        articleName: string,
        helpful: boolean
    ): Promise<{httpResponse: Response; response: HelpfulVoteResponse}> {
        const httpResponse = await new Post({
            params: this.buildHelpfulVoteParams(helpful),
            url: getLinkPath(`/faq/${articleName}/helpful_vote`)
        }).execute();

        const response = await httpResponse.json() as HelpfulVoteResponse;

        return {httpResponse, response};
    }

    private processHelpfulVoteResponse(
        httpResponse: Response,
        response: HelpfulVoteResponse
    ): FaqHelpfulVoteResult {
        if (response.data) {
            this.applyHelpfulVoteData(response.data);
        }

        if (response.success) {
            return {
                data: response.data,
                duplicate: false,
                message: I18n.t('faq.helpful_vote.success'),
                success: true
            };
        }

        if (httpResponse.status === HTTP_STATUS_CONFLICT) {
            return this.helpfulDuplicateResult();
        }

        const message = this.helpfulVoteFailureMessage();

        this.setVoteError(message);

        return {
            data: response.data,
            duplicate: false,
            message,
            success: false
        };
    }

    private helpfulVoteFailureMessage(): string {
        return I18n.t('faq.helpful_vote.failed');
    }

    private helpfulDuplicateResult(): FaqHelpfulVoteResult {
        const message = I18n.t('faq.helpful_vote.duplicate');

        return {
            duplicate: true,
            message,
            success: false
        };
    }

    private applyHelpfulVoteData(data: FaqHelpfulVoteData): void {
        this.rememberGuestVoterKey(data);
    }

    private loadPersistedGuestVoterKey(): string | null {
        if (typeof window === 'undefined') {
            return null;
        }

        return window.localStorage.getItem(FAQ_GUEST_VOTER_KEY_STORAGE);
    }

    private persistGuestVoterKey(voterKey: string): void {
        this.sessionGuestVoterKey = voterKey;

        if (typeof window !== 'undefined') {
            window.localStorage.setItem(FAQ_GUEST_VOTER_KEY_STORAGE, voterKey);
        }
    }

    private rememberGuestVoterKey(data: FaqHelpfulVoteData): void {
        if (!data.guest_voter_id) {
            return;
        }

        this.persistGuestVoterKey(`${GUEST_VOTER_KEY_PREFIX}${data.guest_voter_id}`);
    }
}
